// The browser's "install this app" moment. Chrome and Edge fire
// beforeinstallprompt once, early, and only hand over the prompt to a page
// that kept the event, so this module catches it at start up (main.tsx calls
// initInstallPrompt before React renders) and the install card asks for it
// later. iPhone Safari has no such event: the card explains Share, then Add
// to Home Screen instead.

interface BeforeInstallPromptEvent extends Event {
  prompt(): Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
}

export interface InstallState {
  // The browser can show its own install dialog right now.
  canPrompt: boolean;
  // iPhone or iPad Safari, not yet on the home screen: show the manual steps.
  iosManual: boolean;
  // Already running as an installed app.
  standalone: boolean;
}

// Moments that show someone is enjoying the site; the card waits for one.
export const ENGAGED_EVENT = "truevined:engaged";

let deferred: BeforeInstallPromptEvent | null = null;
let installed = false;
const listeners = new Set<() => void>();
const notify = () => listeners.forEach((listener) => listener());

const isStandalone = () =>
  typeof window !== "undefined" &&
  (window.matchMedia?.("(display-mode: standalone)").matches || (navigator as Navigator & { standalone?: boolean }).standalone === true);

const isIosSafari = () => {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent;
  const ios = /iPhone|iPad|iPod/.test(ua) || (ua.includes("Macintosh") && navigator.maxTouchPoints > 1);
  // Chrome, Firefox and in-app browsers on iOS cannot add to the home screen.
  return ios && /Safari/.test(ua) && !/CriOS|FxiOS|EdgiOS|Instagram|FBAN|FBAV/.test(ua);
};

export function initInstallPrompt() {
  if (typeof window === "undefined") return;
  window.addEventListener("beforeinstallprompt", (event) => {
    event.preventDefault();
    deferred = event as BeforeInstallPromptEvent;
    notify();
  });
  window.addEventListener("appinstalled", () => {
    installed = true;
    deferred = null;
    notify();
  });
}

let snapshot: InstallState = { canPrompt: false, iosManual: false, standalone: false };

// Returns the same object until something changes: useSyncExternalStore
// re-renders forever on a fresh object per call.
export function getInstallState(): InstallState {
  const standalone = installed || isStandalone();
  const canPrompt = Boolean(deferred) && !standalone;
  const iosManual = !standalone && isIosSafari();
  if (snapshot.canPrompt !== canPrompt || snapshot.iosManual !== iosManual || snapshot.standalone !== standalone) {
    snapshot = { canPrompt, iosManual, standalone };
  }
  return snapshot;
}

export function subscribeInstall(listener: () => void) {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

export async function promptInstall(): Promise<"accepted" | "dismissed" | "unavailable"> {
  const event = deferred;
  if (!event) return "unavailable";
  deferred = null;
  notify();
  await event.prompt();
  const choice = await event.userChoice;
  return choice.outcome;
}

export function markEngaged(reason: string) {
  if (typeof window === "undefined") return;
  window.dispatchEvent(new CustomEvent(ENGAGED_EVENT, { detail: reason }));
}
